/**
 * The time register.
 *
 * Two things on this page carry weight:
 *
 * **`locked`** — an entry inside a submitted or approved week cannot be edited,
 * and the row says so before somebody tries. The API is the authority; this is
 * the courtesy.
 *
 * **`invoiced`** — the entry has been pulled onto an invoice line and its hours
 * are on a document a client has. Deleting one would leave an invoice charging
 * for time that no longer exists, which is why the API refuses and why the row
 * shows it.
 *
 * `billable` and `rate` are frozen when the entry is written, not read from the
 * project at display time: a rate change next month must not silently restate
 * what last month cost.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, CalendarDays, Clock, Lock, Plus, Receipt, User } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  Tooltip,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatDuration,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import { SHEET_STATUS_TONES } from '../../lib/tones';
import { TimeEntryDialog } from './time-entry-dialog';

export interface TimeEntryRow {
  public_id: string;
  entry_date: string;
  minutes: number;
  hours: string;
  employee_name: string;
  project_public_id: string;
  project_name: string;
  client_name: string;
  task_title: string | null;
  billable: boolean;
  notes: string | null;
  timesheet_status: string;
  locked: boolean;
  invoiced: boolean;
  created_at: string;
}

interface TimesheetOptions {
  statuses: { value: string; label: string }[];
}

const ENDPOINT = '/time-entries';

export function TimeEntriesPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntryRow | null>(null);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['timesheets', 'options'],
    queryFn: () => api.get<TimesheetOptions>('/timesheets/options'),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<TimeEntryRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<TimeEntryRow>[]>(
    () => [
      {
        field: 'entry_date',
        header: 'Date',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 130,
        format: (value) => formatDate(value as string),
      },
      {
        field: 'employee_name',
        header: 'Who',
        icon: <User className="size-3" />,
        filter: 'select',
        minWidth: 150,
      },
      {
        field: 'project_name',
        header: 'Project',
        filter: 'select',
        minWidth: 190,
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 160,
      },
      { field: 'task_title', header: 'Task', filter: 'select', minWidth: 180 },
      {
        field: 'minutes',
        header: 'Time',
        icon: <Clock className="size-3" />,
        filter: 'number',
        numeric: true,
        width: 100,
        render: (row) => <span className="tabular font-medium">{formatDuration(row.minutes)}</span>,
      },
      {
        field: 'billable',
        header: 'Billable',
        filter: 'boolean',
        align: 'center',
        width: 100,
        render: (row) =>
          row.billable ? (
            <StatusChip value="billable" tones={{ billable: 'success' }} />
          ) : (
            <span className="text-faint">No</span>
          ),
      },
      {
        field: 'timesheet_status',
        header: 'Week',
        filter: 'select',
        options: options.data?.statuses,
        width: 130,
        render: (row) => (
          <span className="flex items-center gap-1.5">
            <StatusChip value={row.timesheet_status} tones={SHEET_STATUS_TONES} />
            {row.locked ? (
              <Tooltip content="The week has been submitted, so its entries are frozen.">
                <Lock className="size-3 text-faint" />
              </Tooltip>
            ) : null}
          </span>
        ),
      },
      {
        field: 'invoiced',
        header: 'Invoiced',
        filter: 'boolean',
        align: 'center',
        width: 100,
        sortable: false,
        render: (row) =>
          row.invoiced ? (
            <Tooltip content="These hours are on an invoice the client has.">
              <Receipt className="size-3.5 text-info" />
            </Tooltip>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      { field: 'notes', header: 'Notes', filter: 'text', minWidth: 220, hiddenByDefault: true },
    ],
    [options.data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Time entries"
        description="Every hour booked, and what has become of it."
        actions={
          <Can permission="timesheets.entry.create">
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Plus className="size-3.5" />
              Book time
            </Button>
          </Can>
        }
      />

      <ServerTable<TimeEntryRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['time-entries']}
        stateKey="timesheets.entries"
        searchPlaceholder="Search by project, client or note"
        onView={(row) => {
          setEditing(row);
          setFormOpen(true);
        }}
        recentKey={recentKey}
        selectable
        onDelete={async (row) => {
          try {
            await api.delete(`${ENDPOINT}/${row.public_id}`);
            toast.success('Entry removed');
          } catch (error) {
            // "the week has been approved", "these hours are invoiced" — the
            // API names what is in the way, so it is shown verbatim.
            toast.error(
              'Could not remove it',
              error instanceof ApiError ? error.message : undefined,
            );
            throw error;
          }
        }}
        deleteLabel={(row) => `${formatDuration(row.minutes)} on ${row.project_name}`}
        rowDetailFields={(row) => [
          { label: 'Employee', value: row.employee_name },
          { label: 'Client', value: row.client_name },
          { label: 'Project', value: row.project_name },
          { label: 'Task', value: row.task_title },
          { label: 'Hours', value: row.hours },
          { label: 'Week', value: row.timesheet_status },
          { label: 'Notes', value: row.notes },
        ]}
        emptyTitle="No time booked"
        emptyDescription="Time is booked against a project, and a week's entries are submitted together for approval."
      />

      <TimeEntryDialog
        open={formOpen}
        entry={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(publicId) => {
          setRecentKey(publicId);
          setFormOpen(false);
        }}
      />
    </div>
  );
}
